import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import UICardActions from '@mui/material/CardActions';
import { LinkButton } from '@backstage/core-components';
import { useTheme } from '@material-ui/core/styles';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import Stack from '@mui/material/Stack';
import CardMedia from '@mui/material/CardMedia';
import { Card } from '../../core/card';
import { CardLinks, CardVariant, DashboardCardProps } from '../../lib/types';

const Owner = ({ owner }: { owner: string }) => {
  const theme = useTheme();

  const catalogApi = useApi(catalogApiRef);

  const entityRef = parseEntityRef(owner, {
    defaultKind: 'Group',
    defaultNamespace: 'default',
  });

  const { value: entity, loading } = useAsync(async () => {
    return await catalogApi.getEntityByRef(entityRef);
  }, []);

  if (loading || !entity) {
    return null;
  }

  return (
    <UICardActions>
      <Stack
        width="100%"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: theme.spacing(0, 1) }}
      >
        <EntityRefLink entityRef={entityRef} underline="none" />
      </Stack>
    </UICardActions>
  );
};

export const DashboardCard = (props: DashboardCardProps) => {
  const theme = useTheme();
  const { actionRoute, actionLabel, target, media, owner } = props;

  const cardTarget = target ?? '_self';

  const cardProps = {
    ...props,
    target: cardTarget,
    variant: CardVariant.minimal,
    actionable: !!(actionRoute || actionLabel),
  };

  const Links = ({ cardLinks }: { cardLinks: CardLinks }) => {
    return (cardLinks || []).map(link => {
      return (
        <LinkButton
          key={link.href}
          to={link.href}
          variant="text"
          color="default"
          size="small"
          underline="none"
        >
          {link.label || link.href}
        </LinkButton>
      );
    });
  };

  return (
    <Card.Root {...cardProps}>
      <Card.Card>
        <Card.Actionable
          cardProps={cardProps}
          style={{ padding: theme.spacing(0) }}
        >
          <Card.Title />
          {media && (
            <CardMedia
              sx={{
                height: 128,
                opacity: 0.9,
              }}
              image={media}
            />
          )}
          {cardProps.description && <Card.Content />}
          {cardProps.actionLabel && <Card.Actions />}
        </Card.Actionable>

        {cardProps.links && (
          <UICardActions>
            <Links cardLinks={cardProps.links} />
          </UICardActions>
        )}
        {owner && <Owner owner={owner} />}
      </Card.Card>
    </Card.Root>
  );
};
